import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, MapPin, Clock, Mail } from "lucide-react";
import { useScrollDirection } from "@/hooks/useScrollDirection";

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const scrollDirection = useScrollDirection();

  const navItems = [
    { label: "Home", href: "/" },
    { label: "Services", href: "/#services" },
    { label: "Portfolio", href: "/#portfolio" },
    { label: "About", href: "/#team" },
    { label: "Blog", href: "/#blog" },
    { label: "Contact", href: "/#contact" },
  ];

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith("/#")) {
      const element = document.getElementById(href.substring(2));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 glass-header ${
        scrollDirection === "down" ? "-translate-y-full" : "translate-y-0"
      }`}
    >
      {/* Top Info Bar */}
      <div className="border-b border-[#00d9ff]/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4 text-[#e0e0e0]/80">
              <a 
                href="tel:8329989595" 
                className="hover:text-[#00d9ff] transition-colors flex items-center gap-1"
                data-testid="link-header-phone"
              >
                <Phone className="h-3 w-3" />
                <span>(832) 998-9595</span>
              </a>
              <span className="hidden sm:inline text-[#e0e0e0]/40">|</span>
              <a 
                href="mailto:info@hhsbuild.com" 
                className="hidden sm:flex hover:text-[#00d9ff] transition-colors items-center gap-1"
                data-testid="link-header-email"
              >
                <Mail className="h-3 w-3" />
                <span>info@hhsbuild.com</span>
              </a>
              <span className="hidden sm:inline text-[#e0e0e0]/40">|</span>
              <span className="hidden sm:inline flex items-center gap-1" data-testid="text-header-location">
                <MapPin className="h-3 w-3" />
                <span>Houston, TX</span>
              </span>
            </div>
            <div className="text-[#e0e0e0]/80 hidden sm:flex items-center gap-3" data-testid="text-header-hours">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>Mon-Fri: 8AM-6PM</span>
              </div>
              <span className="text-[#e0e0e0]/40">|</span>
              <span className="text-[#ffd700] font-semibold" data-testid="text-header-emergency">
                24/7 Emergency Service
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link 
            href="/"
            className="text-2xl md:text-3xl font-heading font-bold gradient-text px-3 py-2 rounded-md transition-all hover:scale-105"
            data-testid="link-home-logo"
          >
            HHS Build
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  if (item.href.startsWith("/#")) {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }
                }}
                className="px-4 py-2 text-[#e0e0e0]/80 hover:text-[#00d9ff] font-medium transition-all rounded-md hover:bg-[#00d9ff]/10"
                data-testid={`link-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              size="default"
              className="font-medium glass-surface border border-[#00d9ff]/40 text-[#00d9ff] hover:glow-cyan transition-all duration-300"
              onClick={() => {
                const element = document.getElementById("contact");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
              data-testid="button-header-cta"
            >
              Get Free Estimate
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#e0e0e0] p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="button-mobile-menu"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden glass-surface border-t border-[#00d9ff]/10" data-testid="mobile-menu">
          <nav className="flex flex-col py-4 px-4">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  if (item.href.startsWith("/#")) {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }
                }}
                className="py-3 px-4 text-[#e0e0e0]/80 hover:text-[#00d9ff] hover:bg-[#00d9ff]/10 rounded-md transition-colors"
                data-testid={`link-mobile-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
            <Button
              size="default"
              className="mt-4 glass-surface border border-[#00d9ff]/40 text-[#00d9ff]"
              onClick={() => {
                setIsMobileMenuOpen(false);
                const element = document.getElementById("contact");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
              data-testid="button-mobile-cta"
            >
              Get Free Estimate
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
